import { reportingTransactions } from "../../lib/allocations";
import { isEarnable, isSpendable, reportableBase, total } from "../../lib/finance";
import { matchesReportingScope, merchantKey, merchantLabel } from "../../lib/reporting";
import type { ReportingActivity } from "../../types/reporting";
import { roundMoney } from "../../lib/money-math";
import type { OwnerFilter, Payee, Transaction } from "../../types";
import type { InsightCategory, InsightDay, InsightMerchant, InsightMonth, InsightTotals } from "./insight-types";
import { insightPeriod, offsetMonth, monthDays, monthLabel, type ComparisonMonths, type TrendMonths } from "./insight-period";

const sum = (entries: Transaction[]): number | null => {
  const value = total(entries);
  return value === null ? null : roundMoney(value);
};
const difference = (a: number | null, b: number | null): number | null => a === null || b === null ? null : roundMoney(a - b);
const add = (a: number | null, b: number | null): number | null => a === null || b === null ? null : roundMoney(a + b);
const rankAmount = (a: { amount: number | null; name: string }, b: { amount: number | null; name: string }) =>
  (b.amount ?? -Infinity) - (a.amount ?? -Infinity) || a.name.localeCompare(b.name);

function groups(entries: Transaction[], key: (entry: Transaction) => string): Map<string, Transaction[]> {
  const result = new Map<string, Transaction[]>();
  for (const entry of entries) {
    const label = key(entry);
    const rows = result.get(label) ?? [];
    rows.push(entry);
    result.set(label, rows);
  }
  return result;
}

/** Reporting uses saved household shares, including bank-pending charges, but never unsynced drafts. */
export function insightsData(transactions: Transaction[], owner: OwnerFilter, month: string, today: string, months: TrendMonths, activity: ReportingActivity = "all", comparisonMonths: ComparisonMonths = 1, payees: Payee[] = []) {
  const period = insightPeriod(month, today);
  const saved = transactions.filter((t) => (owner === "All" || t.owner === owner) && matchesReportingScope(t, today, { activity }, payees));
  const inMonth = (key: string, end: string) => saved.filter((t) => t.date.startsWith(key) && t.date.slice(0, 10) <= end);
  const entries = inMonth(month, period.end);
  const baselineMonths = Array.from({ length: comparisonMonths }, (_, i) => offsetMonth(month, -i - 1));
  const previousPeriods = baselineMonths.map((key) => {
    const days = period.elapsedDays === 0 ? 0 : period.current ? Math.min(period.elapsedDays, monthDays(key)) : monthDays(key);
    return { month: key, days, entries: inMonth(key, `${key}-${String(days).padStart(2, "0")}`) };
  });
  const previousEntries = previousPeriods.flatMap((p) => p.entries);
  const comparable = previousPeriods.every((p) => p.entries.some((t) => isSpendable(t) && t.reportableAmount > 0));
  const average = (rows: Transaction[]) => {
    const value = sum(rows);
    return value === null ? null : roundMoney(value / comparisonMonths);
  };
  if (comparisonMonths === 3) {
    period.previousDays = Math.max(...previousPeriods.map((p) => p.days));
    period.comparisonLabel = `Compared with the average of ${monthLabel(baselineMonths[2], true)}–${monthLabel(baselineMonths[0], true)}${period.current ? `, through day ${period.elapsedDays}` : ""}`;
  }
  const expenses = entries.filter(isSpendable);
  const previousExpenses = previousEntries.filter(isSpendable);
  const income = sum(entries.filter(isEarnable));
  const spending = sum(expenses);
  const previousSpending = average(previousExpenses);
  const change = comparable ? difference(spending, previousSpending) : null;
  const totals: InsightTotals = {
    income, spending, remaining: difference(income, spending),
    dailyAverage: spending === null ? null : period.elapsedDays ? roundMoney(spending / period.elapsedDays) : 0,
    change, changePercent: change === null || !previousSpending ? null : change / previousSpending * 100,
    expenseCount: expenses.filter((t) => t.reportableAmount > 0).length,
    previousCount: comparable ? previousExpenses.length : 0,
    unconvertedCount: [...entries, ...previousEntries].filter((t) => (isSpendable(t) || isEarnable(t)) && reportableBase(t) === null).length,
    pendingCount: entries.filter((t) => t.pending && (isSpendable(t) || isEarnable(t))).length,
    largestPurchases: expenses.filter((t) => (reportableBase(t) ?? 0) > 0).sort((a, b) => (reportableBase(b) ?? 0) - (reportableBase(a) ?? 0)).slice(0, 3),
  };

  const trend: InsightMonth[] = Array.from({ length: months }, (_, i) => {
    const key = offsetMonth(month, i + 1 - months);
    const rows = saved.filter((t) => t.date.startsWith(key));
    const earned = sum(rows.filter(isEarnable)), spent = sum(rows.filter(isSpendable));
    return { month: key, income: earned, spending: spent, remaining: difference(earned, spent), entries: rows.filter((t) => isSpendable(t) || isEarnable(t)).length };
  });

  const byCategory = groups(reportingTransactions(expenses), (t) => t.category || "Uncategorized");
  const previousByCategory = groups(reportingTransactions(previousExpenses), (t) => t.category || "Uncategorized");
  const categories: InsightCategory[] = [...new Set([...byCategory.keys(), ...previousByCategory.keys()])].map((name) => {
    const amount = sum(byCategory.get(name) ?? []), previous = average(previousByCategory.get(name) ?? []);
    return { name, amount, previous, change: comparable ? difference(amount, previous) : null, share: amount === null || !spending ? null : amount / spending * 100 };
  }).filter((row) => row.amount === null || row.previous === null || row.amount > 0 || row.previous > 0).sort(rankAmount);

  // Keep whole purchases here: projecting category splits would count a receipt several times.
  const byMerchant = groups(expenses.filter((t) => t.reportableAmount > 0), (t) => merchantKey(merchantLabel(t, payees)));
  const previousByMerchant = groups(previousExpenses.filter((t) => t.reportableAmount > 0), (t) => merchantKey(merchantLabel(t, payees)));
  const merchantChanges: InsightMerchant[] = [...new Set([...byMerchant.keys(), ...previousByMerchant.keys()])].map((key) => {
    const rows = byMerchant.get(key) ?? [], previous = previousByMerchant.get(key) ?? [];
    const sample = rows[0] ?? previous[0];
    return { key, name: merchantLabel(sample, payees) || "No merchant recorded", amount: sum(rows), count: rows.length,
      previous: average(previous), change: comparable ? difference(sum(rows), average(previous)) : null };
  });
  const merchants = merchantChanges.filter((row) => row.count > 0).sort(rankAmount);
  const daily = (rows: Transaction[]) => groups(rows, (t) => t.date.slice(8, 10));
  const currentDays = daily(expenses), previousDays = daily(previousExpenses);
  let currentTotal: number | null = 0, previousTotal: number | null = 0;
  const comparison: InsightDay[] = Array.from({ length: Math.max(period.elapsedDays, period.previousDays) }, (_, i) => {
    const day = i + 1, key = String(day).padStart(2, "0");
    currentTotal = add(currentTotal, sum(currentDays.get(key) ?? []));
    previousTotal = add(previousTotal, sum(previousDays.get(key) ?? []));
    return { day, current: day <= period.elapsedDays ? currentTotal : null, previous: day <= period.previousDays ? previousTotal : null };
  });
  if (comparisonMonths === 3) {
    for (const row of comparison) {
      row.previous = row.day > period.previousDays || !comparable ? null : average(previousExpenses.filter((t) => Number(t.date.slice(8, 10)) <= row.day));
    }
  }
  return { period, totals, trend, categories, merchants, merchantChanges, comparison, comparable, comparisonMonths, activity };
}
